import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { libraryAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, BookOpenIcon } from '@heroicons/react/24/outline';

// ── Reusable field helpers ────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div>
    <label className="form-label">
      {label}
      {hint && <span className="text-gray-400 font-normal text-xs ml-2">{hint}</span>}
    </label>
    {children}
  </div>
);

// ══════════════════════════════════════════════════════════════
// ADMIN LIBRARY PAGE
// ══════════════════════════════════════════════════════════════
export default function AdminLibraryPage() {
  const [activeTab, setActiveTab] = useState('info');   // 'info' | 'sections'

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">JNF Library</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-100 pb-0">
        {[
          { key: 'info',     label: 'Library Info'  },
          { key: 'sections', label: 'Sections'       },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-xl border-b-2 transition-all -mb-px ${
              activeTab === key
                ? 'border-cyan-400 text-navy-800 bg-white'
                : 'border-transparent text-gray-400 hover:text-navy-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'info'     && <LibraryInfoTab />}
      {activeTab === 'sections' && <LibrarySectionsTab />}
    </AdminLayout>
  );
}

// ── TAB 1: General Library Info ───────────────────────────────
function LibraryInfoTab() {
  const DEFAULTS = { name: 'JNF Library', description: '', address: '', opening_hours: '', phone: '', email: '', cover_url: '' };
  const [form, setForm]     = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    libraryAPI.getInfo().then(r => {
      const d = r.data.data;
      if (d) setForm({ ...DEFAULTS, ...d,
        name:          d.name          || 'JNF Library',
        description:   d.description   || '',
        address:       d.address       || '',
        opening_hours: d.opening_hours || '',
        phone:         d.phone         || '',
        email:         d.email         || '',
        cover_url:     d.cover_url     || '',
      });
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await libraryAPI.updateInfo(form);
      toast.success('Library info updated!');
    } catch {
      toast.error('Failed to save library info.');
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />;

  return (
    <form onSubmit={save} className="max-w-2xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">

      <Field label="Library Name *">
        <input required className="form-input" value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </Field>

      <Field label="Cover / Banner Image URL" hint="(shown as hero at top of library page)">
        <input className="form-input" value={form.cover_url}
          onChange={e => setForm(f => ({ ...f, cover_url: e.target.value }))}
          placeholder="https://..." />
        {form.cover_url && (
          <img src={form.cover_url} alt="preview" className="mt-2 h-28 w-full object-cover rounded-xl border border-gray-100" />
        )}
      </Field>

      <Field label="About / Description">
        <textarea rows={4} className="form-input resize-none" value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Brief description of the JNF Library — its history, collection, purpose..." />
      </Field>

      <Field label="Opening Hours" hint="(shown in the info block)">
        <textarea rows={3} className="form-input resize-none" value={form.opening_hours}
          onChange={e => setForm(f => ({ ...f, opening_hours: e.target.value }))}
          placeholder={`e.g.\nMon – Fri: 9am – 5pm\nSat: 10am – 2pm\nClosed Sundays`} />
      </Field>

      <Field label="Address">
        <input className="form-input" value={form.address}
          onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
          placeholder="e.g. JNF Secretariat, Ilorin, Kwara State" />
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Phone">
          <input className="form-input" value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="+234 800 000 0000" />
        </Field>
        <Field label="Email">
          <input type="email" className="form-input" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="library@jnf.org" />
        </Field>
      </div>

      <button type="submit" disabled={saving} className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2">
        {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
        {saving ? 'Saving...' : 'Save Library Info'}
      </button>
    </form>
  );
}

// ── TAB 2: Library Sections ───────────────────────────────────
const EMPTY_SECTION = {
  name: '', name_ar: '', description: '',
  opening_hours: '', location_note: '', cover_url: '',
  sort_order: 0, is_published: true,
};

function LibrarySectionsTab() {
  const [sections, setSections]   = useState([]);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_SECTION);
  const [saving, setSaving]       = useState(false);

  const fetchSections = () =>
    libraryAPI.getSectionsAdmin().then(r => setSections(r.data.data || []));

  useEffect(() => { fetchSections(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_SECTION, sort_order: sections.length });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      name:          s.name          || '',
      name_ar:       s.name_ar       || '',
      description:   s.description   || '',
      opening_hours: s.opening_hours || '',
      location_note: s.location_note || '',
      cover_url:     s.cover_url     || '',
      sort_order:    s.sort_order    ?? 0,
      is_published:  !!s.is_published,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_SECTION); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Section name is required.'); return; }
    setSaving(true);
    try {
      if (editId) {
        await libraryAPI.updateSection(editId, form);
        toast.success('Section updated!');
      } else {
        await libraryAPI.createSection(form);
        toast.success('Section created!');
      }
      closeForm();
      fetchSections();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save section.');
    } finally { setSaving(false); }
  };

  const togglePublished = async (s) => {
    try {
      await libraryAPI.updateSection(s.id, { is_published: s.is_published ? 0 : 1 });
      toast.success(s.is_published ? 'Section hidden.' : 'Section published!');
      fetchSections();
    } catch {
      toast.error('Failed to update.');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this section? This cannot be undone.')) return;
    try {
      await libraryAPI.deleteSection(id);
      toast.success('Section deleted.');
      fetchSections();
    } catch {
      toast.error('Failed to delete section.');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{sections.length} section{sections.length !== 1 ? 's' : ''}</p>
        <button onClick={openCreate} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> Add Section
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-navy-700">{editId ? 'Edit Section' : 'New Section'}</h2>
            <button type="button" onClick={closeForm} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Section Name *">
              <input required className="form-input" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Tijaniyyah Books" />
            </Field>
            <Field label="Arabic Name" hint="(optional)">
              <input className="form-input" value={form.name_ar}
                onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))}
                style={{ fontFamily: "'Amiri',serif", direction: 'rtl' }}
                placeholder="مثال: كتب التيجانية" dir="rtl" />
            </Field>
          </div>

          <Field label="Section Photo URL" hint="(photo of this shelf/section of the library)">
            <input className="form-input" value={form.cover_url}
              onChange={e => setForm(f => ({ ...f, cover_url: e.target.value }))}
              placeholder="https://..." />
            {form.cover_url && (
              <img src={form.cover_url} alt="preview"
                className="mt-2 h-36 w-full object-cover rounded-xl border border-gray-100" />
            )}
          </Field>

          <Field label="Description">
            <textarea rows={4} className="form-input resize-none" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What books/materials are in this section? What topics does it cover?" />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Opening Hours" hint="(if different from main library)">
              <input className="form-input" value={form.opening_hours}
                onChange={e => setForm(f => ({ ...f, opening_hours: e.target.value }))}
                placeholder="e.g. Mon – Fri: 9am – 5pm" />
            </Field>
            <Field label="Location Note" hint="(where in the library)">
              <input className="form-input" value={form.location_note}
                onChange={e => setForm(f => ({ ...f, location_note: e.target.value }))}
                placeholder="e.g. Ground floor, Room 2" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Sort Order" hint="(lower = shown first)">
              <input type="number" min={0} className="form-input" value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
            </Field>
            <div className="flex items-center gap-3 mt-6">
              <input type="checkbox" id="pub" checked={form.is_published}
                onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))}
                className="h-4 w-4 accent-cyan-500" />
              <label htmlFor="pub" className="text-sm text-navy-700">Published (visible on website)</label>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
              {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Saving...' : editId ? 'Update Section' : 'Save Section'}
            </button>
            <button type="button" onClick={closeForm} className="btn-outline text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      )}

      {/* Sections list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.map(s => (
          <div key={s.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-opacity ${s.is_published ? 'border-gray-100 opacity-100' : 'border-gray-200 opacity-60'}`}>
            <div className="h-36 bg-navy-100 relative overflow-hidden">
              {s.cover_url
                ? <img src={s.cover_url} alt={s.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-navy-600 to-navy-900">📚</div>
              }
              {!s.is_published && (
                <div className="absolute top-2 left-2 bg-gray-800/70 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                  Hidden
                </div>
              )}
            </div>
            <div className="p-4">
              <p className="font-semibold text-navy-800 text-sm mb-1">{s.name}</p>
              {s.name_ar && <p className="arabic text-navy-500 text-sm mb-1">{s.name_ar}</p>}
              {s.description && <p className="text-gray-400 text-xs line-clamp-2 mb-2">{s.description}</p>}
              {s.opening_hours && <p className="text-gray-400 text-xs">🕐 {s.opening_hours}</p>}
              {s.location_note && <p className="text-gray-400 text-xs">📍 {s.location_note}</p>}

              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <button onClick={() => togglePublished(s)}
                  className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${s.is_published ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-600'}`}>
                  {s.is_published ? '✓ Published' : 'Publish'}
                </button>
                <button onClick={() => openEdit(s)} className="p-1.5 text-gray-300 hover:text-navy-500 hover:bg-navy-50 rounded-lg transition-colors" title="Edit">
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button onClick={() => del(s.id)} className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors ml-auto" title="Delete">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {sections.length === 0 && (
          <div className="col-span-full text-center py-16 text-gray-400">
            <BookOpenIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
            <p className="mb-3">No sections added yet.</p>
            <button onClick={openCreate} className="btn-primary text-sm py-2 px-5 inline-flex items-center gap-1">
              <PlusIcon className="h-4 w-4" /> Add First Section
            </button>
          </div>
        )}
      </div>
    </>
  );
}
